import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Copy, Plus, Layers, ArrowRight, CheckCircle2, Package } from 'lucide-react';

export default function TemplatesPage() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showClone, setShowClone] = useState(null);
  const [createForm, setCreateForm] = useState({ project_id: '', name: '', description: '' });
  const [cloneForm, setCloneForm] = useState({ name: '', client_name: '', budget: '', deadline: '' });

  useEffect(() => {
    Promise.all([api.get('/templates'), api.get('/projects')]).then(([t, p]) => {
      setTemplates(t.data);
      setProjects(p.data);
    }).catch(() => {});
  }, [api]);

  const createTemplate = async () => {
    await api.post('/templates', createForm);
    const t = await api.get('/templates');
    setTemplates(t.data);
    setShowCreate(false);
  };

  const cloneTemplate = async () => {
    await api.post(`/templates/${showClone.id}/clone`, { ...cloneForm, budget: parseFloat(cloneForm.budget) || 0 });
    const p = await api.get('/projects');
    setProjects(p.data);
    setShowClone(null);
    setCloneForm({ name: '', client_name: '', budget: '', deadline: '' });
  };

  return (
    <div className="space-y-6" data-testid="templates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Project Templates</h1>
          <p className="text-xs text-zinc-500 mt-1">Save and reuse project structures to save setup time</p>
        </div>
        <button data-testid="create-template-btn" onClick={() => setShowCreate(true)} className="h-8 px-4 bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-sm flex items-center gap-2">
          <Plus className="w-3.5 h-3.5" /> Save as Template
        </button>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="create-template-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96">
            <h3 className="text-sm font-heading font-semibold text-white mb-4">Create Template from Project</h3>
            <div className="space-y-2.5">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Source Project</label>
                <select value={createForm.project_id} onChange={e => setCreateForm({...createForm, project_id: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-zinc-300 px-2">
                  <option value="">Select Project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Template Name</label>
                <input value={createForm.name} onChange={e => setCreateForm({...createForm, name: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3" placeholder="e.g., Standard 2D Series" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase">Description</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} rows={2} className="w-full bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3 py-2" />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={createTemplate} className="flex-1 h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs rounded-sm">Save Template</button>
              <button onClick={() => setShowCreate(false)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Modal */}
      {showClone && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="clone-template-modal">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 w-96">
            <h3 className="text-sm font-heading font-semibold text-white mb-1">Create Project from Template</h3>
            <p className="text-[10px] text-zinc-500 mb-4">Using: {showClone.name}</p>
            <div className="space-y-2.5">
              {[{k:'name',l:'Project Name',p:'New Project Name'},{k:'client_name',l:'Client Name',p:'Client company'},{k:'budget',l:'Budget (INR)',p:'1800000'},{k:'deadline',l:'Deadline',p:'2026-06-30'}].map(f => (
                <div key={f.k}>
                  <label className="text-[10px] text-zinc-500 uppercase">{f.l}</label>
                  <input value={cloneForm[f.k]} onChange={e => setCloneForm({...cloneForm, [f.k]: e.target.value})} className="w-full h-8 bg-zinc-950 border border-zinc-800 rounded-sm text-xs text-white px-3" placeholder={f.p} />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="clone-submit" onClick={cloneTemplate} className="flex-1 h-8 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-sm">Create Project</button>
              <button onClick={() => setShowClone(null)} className="flex-1 h-8 bg-zinc-800 text-zinc-400 text-xs rounded-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {templates.map(t => (
          <div key={t.id} className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 hover:border-zinc-700 transition-colors" data-testid={`template-${t.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-400" />
                <h3 className="text-sm font-semibold text-white">{t.name}</h3>
              </div>
            </div>
            <p className="text-xs text-zinc-500 mb-3">{t.description || 'No description'}</p>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-zinc-800/50 rounded-sm p-2 text-center">
                <p className="text-sm font-heading font-bold text-cyan-400">{t.structure?.episode_count || 0}</p>
                <p className="text-[9px] text-zinc-600">Episodes</p>
              </div>
              <div className="bg-zinc-800/50 rounded-sm p-2 text-center">
                <p className="text-sm font-heading font-bold text-violet-400">{t.structure?.task_pipeline?.length || 6}</p>
                <p className="text-[9px] text-zinc-600">Pipeline Stages</p>
              </div>
            </div>
            <div className="flex gap-1 flex-wrap mb-3">
              {t.structure?.task_pipeline?.map(s => (
                <span key={s} className="text-[8px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded">{s}</span>
              ))}
            </div>
            <button data-testid={`clone-template-${t.id}`} onClick={() => setShowClone(t)} className="w-full h-7 bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-[10px] font-medium rounded-sm hover:bg-emerald-600/30 transition-all flex items-center justify-center gap-1.5">
              <Copy className="w-3 h-3" /> Use Template
            </button>
          </div>
        ))}
        {templates.length === 0 && (
          <div className="col-span-3 bg-zinc-900 border border-zinc-800 rounded-sm p-8 text-center">
            <Layers className="w-10 h-10 text-zinc-800 mx-auto mb-3" />
            <p className="text-xs text-zinc-500">No templates yet. Save a project as a template to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
}
